import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { getStravaActivities, syncStravaActivity, getChallengesByClub } from '../services/api';
import { Activity, Calendar, Clock, TrendingUp, MapPin, ArrowLeft, Plus, CheckCircle } from 'lucide-react';
import { DotLoader } from 'react-spinners';

function StravaActivities() {
  const navigate = useNavigate();
  const location = useLocation();
  const challengeIdFromState = location.state?.challengeId;
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(challengeIdFromState || null);
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const data = await getStravaActivities();
      setActivities(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setError(error.response?.data?.message || 'Erreur lors du chargement des activités');
      setLoading(false);
    }
  };

  const handleSyncActivity = async (activityId) => {
    if (!selectedChallenge) {
      alert('Veuillez sélectionner un challenge d\'abord');
      return;
    }

    setSyncing({ ...syncing, [activityId]: true });

    try {
      await syncStravaActivity(activityId, selectedChallenge);
      alert('✅ Activité ajoutée au challenge !');
      setSyncing({ ...syncing, [activityId]: false });
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de la synchronisation');
      setSyncing({ ...syncing, [activityId]: false });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins.toString().padStart(2, '0')}`;
    }
    return `${mins}min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour au profil
          </button>
          <div className="bg-slate-800 border-2 border-red-500/50 rounded-xl p-6 text-center">
            <p className="text-red-400 font-semibold text-lg">{error}</p>
            <p className="text-sm text-gray-400 mt-2">
              Assurez-vous d'avoir connecté Strava dans votre profil
            </p>
            <button
              onClick={() => navigate('/profile')}
              className="mt-4 px-6 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition"
            >
              Aller au profil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <button
          onClick={() => {
            if (challengeIdFromState) {
              navigate(`/challenge/${challengeIdFromState}`);
            } else {
              navigate('/profile');
            }
          }}
          className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-6 transition"
        >
          <ArrowLeft size={20} className="mr-2" />
          {challengeIdFromState ? 'Retour au challenge' : 'Retour au profil'}
        </button>

        {/* Hero Section */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Mes activités Strava</h1>
              <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">
                {activities.length} activité{activities.length > 1 ? 's' : ''} récente{activities.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex-shrink-0 self-start sm:self-center">
              <div className="bg-indigo-500/10 p-3 sm:p-4 rounded-xl">
                <Activity size={28} className="text-indigo-500 sm:w-8 sm:h-8" />
              </div>
            </div>
          </div>

          {/* Info si challenge sélectionné */}
          {selectedChallenge && (
            <div className="mt-4 p-3 sm:p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                <p className="text-green-400 font-semibold text-sm sm:text-base">
                  Mode ajout au challenge activé
                </p>
              </div>
              <p className="text-xs sm:text-sm text-green-500/80 mt-1">
                Cliquez sur "Ajouter au challenge" pour synchroniser une activité
              </p>
            </div>
          )}
        </div>

        {/* Liste des activités */}
        {activities.length === 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg p-8 sm:p-12 text-center">
            <Activity size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Aucune activité trouvée</h3>
            <p className="text-gray-400 text-sm sm:text-base">
              Allez courir et vos activités apparaîtront ici ! 🏃‍♂️
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="bg-slate-800 border border-slate-700 hover:border-indigo-500/50 rounded-xl shadow-lg p-4 sm:p-6 hover:shadow-xl transition-all"
              >
                {/* Header avec titre et type */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="bg-indigo-500/10 p-2 rounded-lg flex-shrink-0">
                      <Activity size={20} className="text-indigo-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-base sm:text-lg truncate">{activity.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-400 flex items-center gap-1 mt-1">
                        <Calendar size={14} className="flex-shrink-0" />
                        <span className="truncate">{formatDate(activity.start_date)}</span>
                      </p>
                    </div>
                  </div>
                  <span className="bg-indigo-500/20 text-indigo-300 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold flex-shrink-0">
                    {activity.type}
                  </span>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  {/* Distance */}
                  <div className="bg-slate-900 border border-slate-700 p-3 rounded-lg">
                    <div className="flex items-center gap-1.5 text-indigo-400 mb-1">
                      <MapPin size={14} className="flex-shrink-0" />
                      <span className="text-xs font-medium">Distance</span>
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-white">
                      {activity.distance} km
                    </p>
                  </div>

                  {/* Durée */}
                  <div className="bg-slate-900 border border-slate-700 p-3 rounded-lg">
                    <div className="flex items-center gap-1.5 text-green-400 mb-1">
                      <Clock size={14} className="flex-shrink-0" />
                      <span className="text-xs font-medium">Durée</span>
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-white">
                      {formatDuration(activity.moving_time)}
                    </p>
                  </div>

                  {/* Allure */}
                  {activity.pace && (
                    <div className="bg-slate-900 border border-slate-700 p-3 rounded-lg">
                      <div className="flex items-center gap-1.5 text-purple-400 mb-1">
                        <TrendingUp size={14} className="flex-shrink-0" />
                        <span className="text-xs font-medium">Allure</span>
                      </div>
                      <p className="text-lg sm:text-xl font-bold text-white">
                        {activity.pace} min/km
                      </p>
                    </div>
                  )}

                  {/* Dénivelé */}
                  {activity.total_elevation_gain > 0 && (
                    <div className="bg-slate-900 border border-slate-700 p-3 rounded-lg">
                      <div className="flex items-center gap-1.5 text-orange-400 mb-1">
                        <TrendingUp size={14} className="flex-shrink-0" />
                        <span className="text-xs font-medium">D+</span>
                      </div>
                      <p className="text-lg sm:text-xl font-bold text-white">
                        {activity.total_elevation_gain} m
                      </p>
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div className="mt-4 pt-4 border-t border-slate-700 flex flex-col sm:flex-row gap-3">
                  <a
                    href={`https://www.strava.com/activities/${activity.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300 font-semibold text-sm flex items-center justify-center sm:justify-start gap-2 transition"
                  >
                    <Activity size={16} />
                    Voir sur Strava
                  </a>

                  {selectedChallenge && (
                    <button
                      onClick={() => handleSyncActivity(activity.id)}
                      disabled={syncing[activity.id]}
                      className="sm:ml-auto px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition"
                    >
                      {syncing[activity.id] ? (
                        <>
                          <DotLoader size={16} color="white" />
                          Ajout...
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          Ajouter au challenge
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default StravaActivities;